<template>
  <div>
    <SectionTitle>
      <div class="flex justify-between items-center">
        <div>Sync History</div>
        <BaseClearButton @click="dateRangePopover?.$el?.present()" class="m-0 p-0 h-4" color="primary" size="small"
          >{{ selectedRange }}<i class="icon-chevron-down ml-1 text-lg"></i
        ></BaseClearButton>
        <IonPopover ref="dateRangePopover">
          <div class="p-2">
            <IonRadioGroup v-model="selectedRange">
              <IonRadio
                v-for="o of StartOptions"
                :key="o"
                :value="o"
                color="primary"
                @click="dateRangePopover?.$el?.dismiss()"
                justify="space-between"
                class="border-b border-gray-300 p-2 last-of-type:border-none"
                >{{ o }}</IonRadio
              >
            </IonRadioGroup>
          </div>
        </IonPopover>
      </div>
    </SectionTitle>
    <div class="m-2 aspect-video bg-gray-100 rounded-md flex items-center justify-center relative">
      <Transition name="fade">
        <svg v-show="!isLoading" ref="svgRef"></svg>
      </Transition>
      <Transition name="fade" mode="out-in">
        <div v-if="isLoading" class="chart-message">
          <IonSpinner color="primary" size="large" name="crescent"></IonSpinner>
        </div>
        <div v-else-if="noData" class="chart-message text-center">No Data Found<br />In Specified Range</div>
      </Transition>
    </div>
  </div>
</template>

<script lang="ts" setup>
import BaseClearButton from '@/components/forms/BaseClearButton.vue';
import SectionTitle from '@/components/forms/SectionTitle.vue';
import { useToast } from '@/composables/use-toast';
import { ChartService } from '@/http-services/chart.service';
import { IonPopover, IonRadio, IonRadioGroup, IonSpinner } from '@ionic/vue';
import { GatewaySyncChartPoint, NetworkChartsDateRange } from 'common';
import { ref, useTemplateRef, watch } from 'vue';
import {
  select,
  Selection,
  BaseType,
  scaleTime,
  extent,
  scaleLinear,
  max,
  line,
  area,
  axisBottom,
  timeFormat,
  axisLeft,
} from 'd3';
import * as colors from '@/theme/colors.json';

const props = defineProps<{
  propertyId?: string;
  gatewayId?: number;
}>();
const { errorToast } = useToast();
const charts = new ChartService();
const dateRangePopover = useTemplateRef('dateRangePopover');
const StartOptions: NetworkChartsDateRange[] = Object.values(NetworkChartsDateRange);
const selectedRange = ref<NetworkChartsDateRange>(StartOptions[0]);
const isLoading = ref(true);
const noData = ref();
const svgRef = ref();
const width = 500;
const height = 300;
const marginTop = 30;
const marginBottom = 55;
const marginRight = 35;
const marginLeft = 45;

watch(
  [() => props.propertyId, () => props.gatewayId, selectedRange],
  () => {
    fetchChartData();
  },
  { immediate: true }
);

async function fetchChartData() {
  isLoading.value = true;
  if (props.propertyId && props.gatewayId) {
    const { success, error, data } = await charts.getSyncHistory(
      props.propertyId,
      props.gatewayId,
      selectedRange.value
    );
    if (success) {
      noData.value = !data || !data.length;
      drawChart(data);
    } else {
      noData.value = true;
      errorToast(error);
    }
  } else {
    noData.value = true;
  }
  isLoading.value = false;
}

function drawChart(data: GatewaySyncChartPoint[]) {
  const xScale = scaleTime()
    .domain(extent(data, (d) => d.timestamp) as [Date, Date])
    .range([marginLeft, width - marginRight]);
  const maxReadsSynced = max(data, (d) => d.syncedReadCount) || 0;
  const yScale = scaleLinear([0, maxReadsSynced], [height - marginBottom, marginTop]);
  const svg = select(svgRef.value)
    .attr('width', width)
    .attr('height', height)
    .attr('viewBox', [0, 0, width, height])
    .attr('style', 'max-width: 100%; height: auto; height: intrinsic;');
  svg.selectChildren().remove();
  if (!data.length) {
    return;
  }
  const gradient = svg
    .append('defs')
    .append('linearGradient')
    .attr('id', 'blue-gradient')
    .attr('x1', '0%')
    .attr('x2', '0%')
    .attr('y1', '0%')
    .attr('y2', '100%');
  gradient.append('stop').attr('offset', '0%').style('stop-color', colors.blue[300]).style('stop-opacity', 0.5);
  gradient.append('stop').attr('offset', '100%').style('stop-color', colors.blue[300]).style('stop-opacity', 0);

  const l = line<GatewaySyncChartPoint>()
    .x((d) => xScale(d.timestamp))
    .y((d) => yScale(d.syncedReadCount));
  const a = area<GatewaySyncChartPoint>()
    .x((d) => xScale(d.timestamp))
    .y0(yScale(0))
    .y1((d) => yScale(d.syncedReadCount));
  const timeFormatSpec =
    selectedRange.value === NetworkChartsDateRange.TWENTY_FOUR_HOURS ||
    selectedRange.value === NetworkChartsDateRange.PAST_3_DAYS
      ? '%a %_I %p'
      : '%b %e';
  const xTicks = calculateTicks(xScale.domain(), 4);
  const xAxis = axisBottom(xScale)
    .tickFormat(timeFormat(timeFormatSpec) as any)
    .tickSize(0)
    .tickValues(xTicks);
  // draw grid lines
  const yTicks = [0, maxReadsSynced * 0.25, maxReadsSynced * 0.5, maxReadsSynced * 0.75, maxReadsSynced];
  const yAxis = axisLeft(yScale).tickSize(0).tickValues(yTicks);
  for (const gridPercent of yTicks) {
    svg
      .append('line')
      .attr('class', 'gridline')
      .attr('x1', marginLeft)
      .attr('x2', width - marginRight)
      .attr('y1', yScale(gridPercent))
      .attr('y2', yScale(gridPercent))
      .attr('stroke', colors.gray[300])
      .attr('stroke-width', 3);
  }

  // draw gradient area
  svg.append('path').datum(data).attr('stroke', 'none').attr('d', a).style('fill', 'url(#blue-gradient)');
  // draw line
  svg
    .append('path')
    .datum(data)
    .attr('fill', 'none')
    .attr('stroke', colors.blue[500])
    .attr('stroke-width', 3)
    .attr('d', l);

  for (const d of [data[0], data[data.length - 1]]) {
    svg
      .append('circle')
      .attr('class', 'markers')
      .attr('r', 4)
      .attr('cx', xScale(d.timestamp))
      .attr('cy', yScale(d.syncedReadCount))
      .attr('fill', '#fff')
      .attr('stroke', colors.blue[500])
      .attr('stroke-width', 3);
  }

  // draw x axis (but hide default x axis line because our grid line looks better)
  svg
    .append('g')
    .attr('class', 'x-axis')
    .attr('transform', `translate(0,${height - marginBottom + 8})`)
    .call(xAxis)
    .selectAll('line')
    .attr('stroke', colors.gray[500])
    .attr('stroke-width', 1.5);
  svg.selectAll('.x-axis path').attr('stroke', 'none');
  svg
    .selectAll('.x-axis text')
    .attr('color', colors.gray[500])
    .style('font-weight', 'bold')
    .style('font-size', '14px')
    .call(wrap);

  svg
    .append('g')
    .attr('class', 'y-axis')
    .attr('transform', `translate(${marginLeft - 8},0)`)
    .call(yAxis)
    .selectAll('line')
    .attr('stroke', colors.gray[500])
    .attr('stroke-width', 1.5);
  svg.selectAll('.y-axis path').attr('stroke', 'none');
  svg.selectAll('.y-axis text').attr('color', colors.gray[500]).style('font-weight', 'bold').style('font-size', '14px');
}

function calculateTicks(domain: Date[], numTicks: number) {
  const start = domain[0];
  const end = domain[1];
  const tickValues = [];

  // Calculate the interval in milliseconds
  const interval = (end.getTime() - start.getTime()) / (numTicks - 1);

  // Generate tick values
  for (let i = 0; i < numTicks; i++) {
    const tickValue = new Date(start.getTime() + i * interval);
    tickValues.push(tickValue);
  }

  return tickValues;
}

function wrap(text: Selection<BaseType, any, any, any>) {
  text.each(function () {
    const text = select(this);
    const words = text.text().trim().split(/\s+/g);
    if (words.length >= 2) {
      const lineHeight = 1.1; // ems
      const y = text.attr('y');
      const dy = parseFloat(text.attr('dy')) || 0;
      text.text(null);
      text.append('tspan').attr('x', 0).attr('y', y).style('font-size', '12px').text(words[0]);
      text
        .append('tspan')
        .attr('x', 0)
        .attr('y', y)
        .attr('dy', lineHeight + dy + 'em')
        .text(words.slice(1).join(' '));
    }
  });
}
</script>
<style scoped>
.chart-message {
  @apply absolute inset-0 flex items-center justify-center font-medium text-gray-500 text-xl bg-transparent;
}
ion-spinner {
  width: 50px;
  height: 50px;
}
</style>
